/**
 * Codes d'erreur de la base (migration 0045) et des edge functions de contrat,
 * traduits en phrases. La base lève `raise exception 'code'` ; PostgREST renvoie
 * le code dans `error.message`. Un code brut ne doit JAMAIS s'afficher à l'écran.
 *
 * Module pur, sans React ni réseau. `LISIBLE_ONBOARDING` alimente aussi la
 * table `LISIBLE` de `api/supabase.ts` (erreurs des edge functions).
 */
export const LISIBLE_ONBOARDING: Record<string, string> = {
  // Parcours livreur
  livreur_non_actif: "L'inscription de ce livreur n'est pas terminée : il ne peut pas encore prendre de service.",
  transition_interdite: "Ce changement de statut n'est pas permis depuis l'étape actuelle du dossier.",
  motif_requis: 'Un motif est obligatoire pour cette décision.',
  dossier_incomplet:
    "Le dossier est incomplet : toutes les pièces requises doivent être validées et l'assurance ne doit pas être expirée.",
  entretien_non_concluant: "L'entretien n'est pas concluant : aucune recommandation « retenir » n'est enregistrée.",
  formation_non_faite: "La formation n'a pas encore été enregistrée.",
  quiz_non_reussi: "Le quiz n'a pas encore été réussi.",
  contrat_non_signe: "Le contrat n'est pas encore signé.",
  entretien_elimine_pas_retenu: 'Un critère éliminatoire est déclenché : « retenir » est impossible.',
  // Pièces
  casier_trop_ancien:
    "L'extrait de casier judiciaire date de plus de 90 jours : demandez un extrait récent au candidat.",
  casier_date_emission_requise: "La date d'émission de l'extrait de casier judiciaire est obligatoire.",
  casier_date_future: "La date d'émission du casier judiciaire est dans le futur.",
  assurance_expiree: "L'assurance est expirée.",
  assurance_echeance_requise: "La date d'échéance de l'assurance est obligatoire.",
  motif_rejet_requis: 'Indiquez le motif du rejet de la pièce.',
  aucune_piece: "Aucune pièce n'a été déposée.",
  // Commerçant
  compte_reversement_non_verifie: "Le compte de reversement n'est pas encore vérifié.",
  aucun_contrat_publie:
    "Aucun contrat n'est publié pour ce type de partenaire : publiez-en un dans « Modèles de contrat ».",
  exclusivite_non_enregistree:
    "L'exclusivité du commerçant n'est pas enregistrée : le contrat doit d'abord être signé.",
  // Statut juridique et identité (migration 0050)
  identite_incomplete:
    "L'identité juridique du partenaire est incomplète : renseignez le statut et les champs obligatoires avant d'envoyer ou de signer le contrat.",
  identite_verrouillee:
    "L'identité est figée : le contrat est signé ou un lien de signature est parti. Seul le dispatch peut la corriger.",
  statut_juridique_invalide: "Choisissez un statut juridique valable (un livreur ne peut pas être une société).",
  forme_juridique_invalide: 'Forme juridique inconnue.',
  type_piece_invalide: "Type de pièce d'identité inconnu.",
  capital_invalide: 'Le capital doit être un nombre entier de FCFA supérieur à zéro.',
  date_naissance_invalide: 'Date de naissance invalide : saisissez-la au format JJ/MM/AAAA, dans le passé.',
  personne_mineure: 'La personne doit être majeure (18 ans révolus) pour signer un contrat.',
  champ_trop_long: 'Un des champs est trop long (300 caractères au plus).',
  identite_invalide: "L'identité envoyée est illisible. Rechargez la page.",
  pas_livreur: "Ce compte n'a pas de fiche livreur.",
  marchand_introuvable: 'Établissement introuvable.',
  contrat_marques_inconnues:
    'Le modèle de contrat publié contient des {{marques}} inconnues : corrigez-le dans « Modèles de contrat » et publiez une nouvelle version.',
  texte_rendu_requis: "Le contrat doit être affiché avant d'être accepté.",
  texte_rendu_perime: "Le texte affiché n'est plus à jour (identité modifiée ou délai dépassé) : rechargez le contrat.",
  // Quiz
  quiz_non_accessible: "Le quiz n'est pas encore accessible : la formation n'a pas été suivie.",
  quiz_bloque: 'Le quiz est bloqué après 3 échecs : débloquez-le depuis la fiche du livreur.',
  quiz_vide: "Aucune question de quiz n'est active.",
  reponses_invalides: 'Les réponses au quiz sont invalides.',
  // Contrats
  contrat_introuvable: 'Contrat introuvable.',
  contrat_obsolete: 'Une version plus récente du contrat est publiée : rechargez la page.',
  etape_non_atteinte: "Le candidat n'a pas encore atteint l'étape de signature du contrat.",
  marchand_requis: 'Un établissement doit être précisé pour ce contrat.',
  forbidden: "Vous n'avez pas le droit d'effectuer cette action — elle est réservée à un administrateur ou à un membre autorisé.",
  signature_documenso_en_cours:
    "Un lien de signature électronique a déjà été envoyé : utilisez-le (il est affiché dans la section Contrat).",
  contrat_publie_immuable: 'Une version publiée ne se modifie plus : créez un nouveau brouillon.',
  contrat_provisoire_non_publiable:
    'Ce contrat est marqué « texte provisoire » : remplacez le texte puis décochez la case avant de publier.',
  contrat_publier_apres_creation: "Créez d'abord le brouillon, relisez-le, puis publiez-le.",
  contrat_exclusivite_publie: "Un contrat commerçant exige une durée d'exclusivité (en mois) pour être publié.",
  contrat_exclusivite_livreur: "Un contrat livreur n'a pas de durée d'exclusivité.",
  non_authentifie: 'Session expirée — reconnectez-vous.',
  // CGU des clients (migration 0052)
  cgu_publiee_immuable: 'Une version publiée ne se modifie plus : créez un nouveau brouillon.',
  cgu_provisoire_non_publiable:
    'Ce texte est marqué « provisoire » : faites-le relire par le juriste, puis décochez la case avant de publier.',
  cgu_publier_apres_creation: "Créez d'abord le brouillon, relisez-le, puis publiez-le.",
  cgu_publier_par_la_fonction: 'Une version se publie avec le bouton « Publier », pas autrement.',
  cgu_identique: 'Ce texte est identique à celui qui est déjà en vigueur : modifiez-le avant de publier.',
  cgu_deja_publiee: 'Cette version est déjà publiée.',
  cgu_introuvable: 'Version des conditions générales introuvable.',
  // Documenso (edge function envoyer-contrat-signature)
  documenso_non_configure:
    "Documenso n'est pas encore installé/configuré : voir docs/onboarding/DOCUMENSO.md — secrets DOCUMENSO_URL, DOCUMENSO_API_KEY, DOCUMENSO_WEBHOOK_SECRET à poser dans Supabase.",
  documenso_erreur: "Documenso n'a pas répondu correctement : réessayez dans un moment.",
}

export const CODE_DOCUMENSO_NON_CONFIGURE = 'documenso_non_configure'

const ESCAPE = /[.*+?^${}()|[\]\\]/g
/** Codes triés du plus long au plus court : `contrat_exclusivite_publie` avant tout préfixe commun. */
const CODES = Object.keys(LISIBLE_ONBOARDING).sort((a, b) => b.length - a.length)

/** Le code de la table reconnu dans un message, ou null. */
export function codeErreur(message: string): string | null {
  for (const code of CODES) {
    const motif = new RegExp(`(^|[^a-z0-9_])${code.replace(ESCAPE, '\\$&')}([^a-z0-9_]|$)`)
    if (motif.test(message)) return code
  }
  return null
}

/**
 * Traduit un message d'erreur de la base en phrase claire.
 *  • code connu → sa phrase ;
 *  • violation de RLS → « pas le droit » ;
 *  • autre code technique en snake_case → phrase générique (jamais le code seul) ;
 *  • message déjà lisible (réseau…) → conservé.
 */
export function traduireErreurOnboarding(message: string | null | undefined): string {
  const brut = (message ?? '').trim()
  if (!brut) return 'Opération impossible.'
  const code = codeErreur(brut)
  if (code) return LISIBLE_ONBOARDING[code] ?? brut
  if (/row-level security/i.test(brut)) {
    return "Vous n'avez pas le droit d'effectuer cette action (règles de sécurité de la base)."
  }
  if (/duplicate key|unique constraint/i.test(brut)) {
    return 'Cet enregistrement existe déjà : rechargez la page.'
  }
  if (/^[a-z][a-z0-9_]*$/.test(brut)) return "L'opération a été refusée par la base."
  return brut
}

/** Message lisible pour n'importe quelle erreur levée (Error, erreur PostgREST, texte…). */
export function messageErreur(e: unknown): string {
  if (e instanceof Error) return traduireErreurOnboarding(e.message)
  if (typeof e === 'object' && e !== null && 'message' in e) {
    return traduireErreurOnboarding(String((e as { message: unknown }).message))
  }
  return 'Opération impossible.'
}
